/**
 * Service API pour la récupération et l'évaluation des règles de messages
 * Utilisé par le renderer pour afficher dynamiquement les messages d'erreur/warning/devis
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cache pour les règles (évite des appels API répétés)
let cachedRules = null;
let cachedSettings = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function preprocessFormulaAngles(formula) {
  if (typeof formula !== 'string') return formula;
  return formula
    .replace(/(\d+(?:\.\d+)?)\s*(?:radians?|rad)\b/gi, '$1')
    .replace(/(\d+(?:\.\d+)?)\s*(?:degrees?|deg|°)/gi, '($1 * Math.PI / 180)');
}

const MESSAGE_RULE_MATH_PROXY = new Proxy(Math, {
  get(target, prop) {
    if (prop === 'radians') return (deg) => (deg * Math.PI) / 180;
    return Reflect.get(target, prop);
  },
});

/**
 * Récupère les règles de messages et les paramètres globaux depuis le backend
 * @param {boolean} forceRefresh - Force le rechargement depuis l'API
 * @returns {Promise<{rules: Array, settings: Object}>}
 */
export const fetchMessageRules = async (forceRefresh = false) => {
  // Vérifier le cache
  if (!forceRefresh && cachedRules && cachedSettings && cacheTimestamp) {
    if (Date.now() - cacheTimestamp < CACHE_TTL) {
      return { rules: cachedRules, settings: cachedSettings };
    }
  }

  try {
    const response = await fetch(`${BACKEND_URL}/message-rules/public`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch message rules: ${response.status}`);
    }

    const data = await response.json();
    
    // Mettre en cache
    cachedRules = data.rules || [];
    cachedSettings = data.settings || {};
    cacheTimestamp = Date.now();

    return { rules: cachedRules, settings: cachedSettings };
  } catch (error) {
    console.error('[MessageRulesApi] Error fetching message rules:', error);
    // Retourner le cache même s'il est périmé, sinon des valeurs par défaut
    if (cachedRules && cachedSettings) {
      return { rules: cachedRules, settings: cachedSettings };
    }
    return { rules: [], settings: { maxDimensionLimit: 2000 } };
  }
};

/**
 * Évalue une expression de règle avec le contexte fourni
 * @param {string} expression - Expression JavaScript à évaluer
 * @param {Object} context - Variables disponibles pour l'évaluation
 * @returns {boolean} Résultat de l'évaluation
 */
// Variables exposées (sync avec usipanel-backoffice/app/(dashboard)/message-rules/page.jsx).
//   - historiques minuscules : L, H, P, LU, HU, P1U, P2U, type, bottom, selectedTab,
//     noClipX, noClipY, noClipZP1, noClipZP2, hasMatchingProduct, MAX_DIMENSION
//   - alias majuscules alignés sur le reste du backoffice/renderer :
//     INSTALL (= type), BOTTOM (= bottom), WALLMOUNT (dérivé INSTALL),
//     DEC / DECi (resolveDecValues), DECc (null ici)
export const evaluateExpression = (expression, context) => {
  if (!expression || expression.trim() === '') return false;

  try {
    const {
      L, H, P, LU, HU, P1U, P2U,
      type, bottom, selectedTab,
      noClipX, noClipY, noClipZP1, noClipZP2,
      hasMatchingProduct, MAX_DIMENSION
    } = context;

    const INSTALL = context.INSTALL ?? type ?? null;
    const BOTTOM = context.BOTTOM ?? !!bottom;
    const WALLMOUNT = context.WALLMOUNT != null
      ? !!context.WALLMOUNT
      : (INSTALL === 'sol-mur' || INSTALL === 'mur' || INSTALL === 'mur-haut');
    const { DECi, DECc, DEC } = resolveMessageRuleDec(INSTALL, context);

    const safeExpression = preprocessFormulaAngles(expression);
    // eslint-disable-next-line no-new-func -- Nécessaire pour évaluer les expressions dynamiques des règles backend
    const fn = new Function(
      'L', 'H', 'P', 'LU', 'HU', 'P1U', 'P2U',
      'type', 'bottom', 'selectedTab',
      'noClipX', 'noClipY', 'noClipZP1', 'noClipZP2',
      'hasMatchingProduct', 'MAX_DIMENSION',
      'INSTALL', 'BOTTOM', 'WALLMOUNT', 'DEC', 'DECi', 'DECc',
      'Math', 'math',
      `return !!(${safeExpression});`
    );

    return fn(
      L, H, P, LU, HU, P1U, P2U,
      type, bottom, selectedTab,
      noClipX, noClipY, noClipZP1, noClipZP2,
      hasMatchingProduct, MAX_DIMENSION,
      INSTALL, BOTTOM, WALLMOUNT, DEC, DECi, DECc,
      Math, MESSAGE_RULE_MATH_PROXY
    );
  } catch (error) {
    // Surface explicite des erreurs (ReferenceError, SyntaxError, etc.) pour
    // que les règles désactivées silencieusement deviennent visibles côté client.
    console.warn(`[MessageRulesApi] Expression invalide « ${expression} » :`, error.message);
    return false;
  }
};

// Résolution DEC autonome côté renderer — sync helpers/decHelpers.js
// (sol/sol-mur=-10, mur/mur-haut=+10). On n'importe pas decHelpers pour éviter
// une dépendance croisée et garder cette fonction utilisable hors React.
function resolveMessageRuleDec(install, context) {
  const map = { sol: -10, 'sol-mur': -10, mur: 10, 'mur-haut': 10 };
  const DECi = context.DECi ?? (install && map[install] !== undefined ? map[install] : 10);
  const DECc = context.DECc ?? null;
  const DEC = context.DEC ?? DECi;
  return { DECi, DECc, DEC };
}

/**
 * Évalue toutes les règles et retourne la première qui correspond
 * @param {Array} rules - Liste des règles triées par priorité
 * @param {Object} context - Contexte d'évaluation
 * @returns {Object|null} Règle correspondante ou null
 */
export const evaluateMessageRules = (rules, context) => {
  if (!rules || rules.length === 0) return null;

  // Les règles sont déjà triées par priorité (du backend)
  for (const rule of rules) {
    if (!rule.enabled) continue;

    const result = evaluateExpression(rule.expression, context);

    if (result) {
      return rule;
    }
  }

  return null;
};

/**
 * Fonction principale pour obtenir le message à afficher
 * Combine fetch + évaluation en une seule fonction pratique
 * @param {Object} context - Contexte complet d'évaluation
 * @returns {Promise<{rule: Object|null, settings: Object}>}
 */
export const getActiveMessageRule = async (context) => {
  const { rules, settings } = await fetchMessageRules();
  
  // Ajouter MAX_DIMENSION au contexte depuis les settings
  const fullContext = {
    ...context,
    MAX_DIMENSION: settings.maxDimensionLimit || 2000,
  };

  const rule = evaluateMessageRules(rules, fullContext);
  
  return { rule, settings };
};

/**
 * Vide le cache (utile pour forcer un rechargement)
 */
export const clearCache = () => {
  cachedRules = null;
  cachedSettings = null;
  cacheTimestamp = null;
};

/**
 * Précharge les règles (à appeler au démarrage de l'application)
 */
export const preloadMessageRules = async () => {
  try {
    await fetchMessageRules(true);
  } catch (error) {
    console.warn('[MessageRulesApi] Failed to preload rules:', error);
  }
};
